import { useLazyQuery, useMutation } from '@apollo/client';
import { IGetPaymentResult, PaymentMethod } from '@monofront/types';
import {
  ATTACH_PAYMENT_METHOD,
  GET_PAYMENT,
} from '@monofront/business/queries';
import { usePaymentMethods } from './PaymentMethodContext';
import { trackEvent } from '@monofront/tracking';

interface IAddPaymentMethodHook {
  handleAddPaymentMethod: (
    paymentMethodId: string,
    paymentMethodSource: string,
    financialPaymentId?: string,
  ) => Promise<void>;
  handleAddNewCard: (
    financialPaymentId: string,
    paymentMethodSource: string,
  ) => Promise<void>;
}

const useAddPaymentMethod = (
  withAutoRefresh = false,
  source: string,
): IAddPaymentMethodHook => {
  const [attachPaymentMethod] = useMutation<{
    attachPaymentMethod: PaymentMethod;
  }>(ATTACH_PAYMENT_METHOD);
  const [getPayment] = useLazyQuery<IGetPaymentResult>(GET_PAYMENT);

  const { refreshList } = usePaymentMethods();

  const handleAddPaymentMethod = async (
    paymentMethodId: string,
    paymentMethodSource: string,
    financialPaymentId?: string,
  ): Promise<void> => {
    await attachPaymentMethod({
      variables: {
        paymentMethodId,
        paymentMethodSource,
        financialPaymentId,
      },
    });

    trackEvent({
      type: 'LFPaymentMethodUpdated',
      data: {
        payment_method_action: 'added',
        update_source: source,
      },
    });

    if (withAutoRefresh) {
      await refreshList();
    }
  };

  const handleAddNewCard = async (
    financialPaymentId: string,
    paymentMethodSource: string,
  ): Promise<void> => {
    const { data } = await getPayment({
      variables: {
        paymentId: financialPaymentId,
      },
    });

    const paymentMethodId = data?.getPayment?.paymentMethodId;

    if (!paymentMethodId) {
      throw new Error('[payment] Cannot retrieve new Payment to attach it.');
    }

    await handleAddPaymentMethod(
      paymentMethodId,
      paymentMethodSource,
      financialPaymentId,
    );
  };

  return { handleAddPaymentMethod, handleAddNewCard };
};

export default useAddPaymentMethod;
